import ky, { type KyInstance } from 'ky';
import { useAuthStore, readAccessToken, readPreMfaToken, readRefreshToken } from '@/stores/auth';
import { toApiError } from './errors';
import { refreshResponseSchema } from '@/schemas/auth';
import { isMfaVerified } from '@/lib/jwt';

/**
 * Routes that don't take a bearer at all — they either issue tokens or
 * carry their own credential in the body.
 */
const PUBLIC_AUTH_PATHS = new Set<string>([
  'v1/auth/login',
  'v1/auth/forgot-password',
  'v1/auth/reset-password',
  'v1/auth/refresh',
  'v1/auth/verify-email',
  'v1/auth/logout',
]);

/** /v1/auth/mfa-challenge takes the pre-MFA bearer instead of the access token. */
const PRE_MFA_BEARER_PATH = 'v1/auth/mfa-challenge';

/**
 * Endpoints that are authenticated but don't require MFAVerified=true.
 * They must accept the pre-MFA bearer when a full access token isn't available
 * (e.g. during first-time enrollment, before any MFA has been completed).
 */
const PRE_MFA_FALLBACK_PATHS = new Set<string>([
  'v1/me/mfa/enroll',
  'v1/me/mfa/verify',
  'v1/me/mfa/disable',
]);

const apiBase: string = (() => {
  const raw = import.meta.env.VITE_API_BASE_URL;
  if (!raw || raw === 'undefined') return 'http://localhost:8080/';
  return raw.endsWith('/') ? raw : `${raw}/`;
})();

function pathOf(input: string | URL | Request): string {
  if (typeof input === 'string') {
    if (input.startsWith('http')) return new URL(input).pathname.replace(/^\/+/, '');
    return input.replace(/^\/+/, '');
  }
  if (input instanceof URL) return input.pathname.replace(/^\/+/, '');
  return new URL(input.url).pathname.replace(/^\/+/, '');
}

/**
 * Single in-flight refresh — additional 401s during a refresh wait on it
 * instead of triggering parallel rotations (refresh tokens are rotated, so
 * concurrent refreshes would invalidate each other).
 */
let refreshInFlight: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  const refreshToken = readRefreshToken();
  if (!refreshToken) return null;
  try {
    const raw = await ky
      .post('v1/auth/refresh', {
        prefixUrl: apiBase,
        json: { refresh_token: refreshToken },
        retry: 0,
        timeout: 10_000,
      })
      .json<unknown>();
    const parsed = refreshResponseSchema.parse(raw);
    // Per docs/api/auth/refresh.md: the new access token has MFAVerified=false.
    // We persist the new refresh token, but the access token cannot satisfy
    // /admin/v1/* until the user re-attests via /mfa.
    useAuthStore.setState({
      refreshToken: parsed.refresh_token,
      userId: parsed.user_id,
      // Hold the new access token as preMfa — it's effectively pre-MFA.
      preMfaToken: parsed.access_token,
      accessToken: null,
      accessExpiresAt: parsed.expires_at,
      status: 'needs_mfa',
    });
    return parsed.access_token;
  } catch {
    // Refresh dead — wipe state and force re-login.
    useAuthStore.getState().reset();
    return null;
  }
}

function refreshAccessToken(): Promise<string | null> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/**
 * Public entry point for triggering a session refresh from route guards.
 * Returns once the in-flight refresh resolves so callers can read the
 * updated auth state synchronously afterwards.
 */
export async function bootstrapSession(): Promise<void> {
  const state = useAuthStore.getState();
  if (state.status !== 'anonymous' || !state.refreshToken) return;
  await refreshAccessToken();
}

export function createApiClient(): KyInstance {
  return ky.create({
    prefixUrl: apiBase,
    timeout: 20_000,
    retry: 0,
    hooks: {
      beforeRequest: [
        (request) => {
          const path = pathOf(request.url);
          if (PUBLIC_AUTH_PATHS.has(path)) return;

          const token =
            path === PRE_MFA_BEARER_PATH
              ? readPreMfaToken()
              : PRE_MFA_FALLBACK_PATHS.has(path)
                ? (readAccessToken() ?? readPreMfaToken())
                : readAccessToken();
          if (token) request.headers.set('Authorization', `Bearer ${token}`);
        },
      ],
      afterResponse: [
        async (request, _options, response) => {
          if (response.ok) return response;
          const path = pathOf(request.url);

          // 401 from an admin call → attempt refresh + retry exactly once.
          // Do NOT retry on 401 from auth endpoints — they're terminal.
          if (response.status === 401 && !PUBLIC_AUTH_PATHS.has(path) && path !== PRE_MFA_BEARER_PATH) {
            const newToken = await refreshAccessToken();
            // Refresh always returns MFAVerified=false. We can only retry the
            // original request if the freshly-issued access token is somehow
            // already verified (won't happen with the documented auth flow,
            // but if a future endpoint changes, we won't be wrong).
            if (newToken && isMfaVerified(newToken)) {
              request.headers.set('Authorization', `Bearer ${newToken}`);
              return ky(request);
            }
            // Otherwise the user must re-MFA. The needs_mfa state is set inside
            // performRefresh; the routing layer will redirect.
          }

          // 403 on admin calls means MFAVerified=false against an admin gate.
          // Mark needs_mfa so the layout redirects to /mfa.
          if (response.status === 403 && !PUBLIC_AUTH_PATHS.has(path)) {
            useAuthStore.getState().setNeedsMfa();
          }
          return response;
        },
      ],
      beforeError: [
        // We intentionally don't transform here — callers wrap with toApiError
        // to keep the schema validation/parse path consistent.
        (error) => error,
      ],
    },
  });
}

export const api = createApiClient();

/**
 * Run a request and parse the response with a Zod schema. Wraps ky errors
 * as ApiError for downstream form/UI handling.
 */
export async function request<TOut>(
  fn: () => Promise<unknown>,
  schema: { parse: (raw: unknown) => TOut }
): Promise<TOut> {
  let raw: unknown;
  try {
    raw = await fn();
  } catch (e) {
    throw await toApiError(e);
  }
  try {
    return schema.parse(raw);
  } catch (e) {
    // A 200 whose body breaks the schema is a contract mismatch, not bad user
    // input: it gets its own code, is never retried (the same bytes fail the
    // same way), and carries the Zod issue paths so the screen can say *what*
    // did not match instead of "one of the fields is invalid".
    throw await toApiError(e);
  }
}
